
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CartItem, ShippingOption } from '../services/cep';

interface CartState {
  items: CartItem[];
  shippingAddress: ShippingAddress | null;
  selectedShipping: ShippingOption | null;
  customerInfo: CustomerInfo | null;
  couponCode: string;
  
  // Actions
  addItem: (item: CartItem) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  setShippingAddress: (address: ShippingAddress) => void;
  setSelectedShipping: (option: ShippingOption | null) => void;
  setCustomerInfo: (info: CustomerInfo) => void;
  setCouponCode: (code: string) => void;
  
  // Computed
  getSubtotal: () => number;
  getTotal: () => number;
  getItemCount: () => number;
}

export interface ShippingAddress {
  cep: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
}

export interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  document: string;
  saveInfo: boolean;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      shippingAddress: null,
      selectedShipping: null,
      customerInfo: null,
      couponCode: '',
      
      addItem: (item: CartItem) => {
        const { items } = get();
        const existingItem = items.find(i => i.id === item.id);
        
        if (existingItem) {
          set({
            items: items.map(i => 
              i.id === item.id 
                ? { ...i, quantity: (i.quantity || 1) + (item.quantity || 1) } 
                : i
            )
          });
        } else {
          set({ items: [...items, { ...item, quantity: item.quantity || 1 }] });
        }
      },
      
      removeItem: (itemId: string) => {
        const { items } = get();
        set({ items: items.filter(i => i.id !== itemId) });
      },
      
      updateQuantity: (itemId: string, quantity: number) => {
        const { items } = get();
        if (quantity <= 0) {
          set({ items: items.filter(i => i.id !== itemId) });
        } else {
          set({
            items: items.map(i => 
              i.id === itemId ? { ...i, quantity } : i
            )
          });
        }
      },
      
      clearCart: () => set({ items: [] }),
      
      setShippingAddress: (address: ShippingAddress) => set({ shippingAddress: address }),
      
      setSelectedShipping: (option: ShippingOption | null) => set({ selectedShipping: option }),
      
      setCustomerInfo: (info: CustomerInfo) => set({ customerInfo: info }),
      
      setCouponCode: (code: string) => set({ couponCode: code }),
      
      getSubtotal: () => {
        const { items } = get();
        return items.reduce(
          (sum, item) => sum + ((item.discountedPrice || item.price) * (item.quantity || 1)), 
          0
        );
      },
      
      getTotal: () => {
        const { getSubtotal, selectedShipping } = get();
        const shippingCost = selectedShipping ? selectedShipping.price : 0;
        return getSubtotal() + shippingCost;
      },
      
      getItemCount: () => {
        const { items } = get();
        return items.reduce((sum, item) => sum + (item.quantity || 1), 0);
      }
    }),
    {
      name: 'ink-shop-cart',
    }
  )
);
